/**
 * Help-assistant REST client (AB#26561) - chats/feedback/actions endpoints. Companion to
 * chat-core.js (pure UI-side logic) and sse-client.js (the chat-stream endpoint); this module
 * covers the chat-history and agentic-action REST surface only.
 *
 * See docs/work/AB26561-chat-docs-help-poc/chat-core-contract.md for the wire contract (DTO
 * shapes, status codes, endpoint table) this client speaks.
 */

/**
 * @typedef {object} ChatSummary
 * @property {string} chatId
 * @property {string} title
 * @property {string} createdAt ISO 8601, UTC.
 * @property {string} updatedAt ISO 8601, UTC.
 */

/**
 * @typedef {object} ChatDetail
 * @property {string} chatId
 * @property {string} title
 * @property {string} createdAt
 * @property {string} updatedAt
 * @property {{ messageId: number, role: 'user'|'assistant', content: string, createdAt: string, rating: 'up'|'down'|null }[]} messages Oldest first.
 */

const CHATS_PATH = '/api/help/chats'
const ACTIONS_PATH = '/api/help/actions'

/** Throws for any non-ok response; callers that treat specific statuses specially (getChat's
 * 404, the boolean-returning action/feedback calls) check `res.ok`/`res.status` before reaching
 * this helper. */
function assertOk (res) {
	if (!res.ok) throw new Error('help_api_' + res.status)
}

/**
 * Builds the help-assistant REST client.
 * @param {object} options
 * @param {string} [options.baseUrl] Prepended to every request path; defaults to same-origin ('').
 * @param {() => string} options.getToken Returns the current bearer token; called fresh per request.
 * @param {typeof fetch} [options.fetchImpl] Defaults to the global `fetch`.
 */
export function createHelpApiClient ({ baseUrl = '', getToken, fetchImpl = fetch } = {}) {
	const headers = () => ({
		authorization: `Bearer ${getToken()}`,
		'content-type': 'application/json'
	})

	const chatsUrl = (suffix = '') => `${baseUrl}${CHATS_PATH}${suffix}`
	const actionsUrl = (suffix = '') => `${baseUrl}${ACTIONS_PATH}${suffix}`
	const encodedId = id => encodeURIComponent(id)

	return {
		/** @returns {Promise<ChatSummary[]>} */
		async listChats () {
			const res = await fetchImpl(chatsUrl(), { headers: headers() })
			assertOk(res)
			return res.json()
		},

		/**
		 * @param {string} [title]
		 * @returns {Promise<ChatSummary>}
		 */
		async createChat (title) {
			const res = await fetchImpl(chatsUrl(), {
				method: 'POST',
				headers: headers(),
				body: JSON.stringify(title !== undefined ? { title } : {})
			})
			assertOk(res)
			return res.json()
		},

		/**
		 * @param {string} chatId
		 * @returns {Promise<ChatDetail|null>} `null` when the chat does not exist (404).
		 */
		async getChat (chatId) {
			const res = await fetchImpl(chatsUrl(`/${encodedId(chatId)}`), { headers: headers() })
			if (res.status === 404) return null
			assertOk(res)
			return res.json()
		},

		/**
		 * @param {string} chatId
		 * @returns {Promise<void>}
		 */
		async deleteChat (chatId) {
			const res = await fetchImpl(chatsUrl(`/${encodedId(chatId)}`), { method: 'DELETE', headers: headers() })
			assertOk(res)
		},

		/**
		 * Best-effort thumbs feedback: never throws, resolves to whether the server accepted it.
		 * @param {string} chatId
		 * @param {{ messageId: number, rating: 'up'|'down', screenContext?: string }} feedback
		 * @returns {Promise<boolean>}
		 */
		async sendFeedback (chatId, feedback) {
			const res = await fetchImpl(chatsUrl(`/${encodedId(chatId)}/feedback`), {
				method: 'POST',
				headers: headers(),
				body: JSON.stringify(feedback)
			})
			return res.ok
		},

		/**
		 * Confirms a proposed write action (where the real SAPI write happens server-side). Never
		 * throws, resolves to whether the server applied it.
		 * @param {string} actionId
		 * @returns {Promise<boolean>}
		 */
		async confirmAction (actionId) {
			const res = await fetchImpl(actionsUrl(`/${encodedId(actionId)}/confirm`), {
				method: 'POST',
				headers: headers()
			})
			return res.ok
		},

		/**
		 * Dismisses/rejects a proposed action. Fire-and-forget by contract: the server always
		 * answers 204 (see chat-core-contract.md), and callers (the panel orchestrator) invoke
		 * this unawaited, so it never throws - an unhandled rejection from a fire-and-forget call
		 * would otherwise land in the browser console with nothing to catch it.
		 * @param {string} actionId
		 * @returns {Promise<void>}
		 */
		async dismissAction (actionId) {
			try {
				await fetchImpl(actionsUrl(`/${encodedId(actionId)}`), { method: 'DELETE', headers: headers() })
			} catch {
				// Fire-and-forget: a network failure here is not actionable by the caller (see
				// doc comment above), so swallow it rather than let it reject.
			}
		}
	}
}
